import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Col, Form, Modal, Row, Table, Tab, Tabs } from 'react-bootstrap';
import { FaPencilAlt, FaBan, FaExternalLinkAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import ApiService from 'helpers/apiService';
import { useProject } from 'contexts/ProjectContext';
import type { ProjectDTO, ProjectData, ProjectRoleEntry, ServiceConnectDTO } from './types';
import { defaultProjectData } from './types';
import ChatWidgetTab from './ChatWidgetTab';
import KycSettingsTab from './KycSettingsTab';
import paths from 'routes/paths';
import siteMapRoutes from 'routes/siteMaps';
import { SERVICE_NODES } from 'components/service-map/data/initialNodes';

function formatProjectError(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
  if (e?.response?.status === 404) {
    return 'Сервис проектов недоступен (404). Убедитесь, что на бэкенде реализованы эндпоинты POST/GET/PUT /project/v1';
  }
  return (e?.response?.data?.message ?? e?.message) || 'Не удалось сохранить проект. Попробуйте позже.';
}

const THEME_OPTIONS = [
  { value: '', label: 'По умолчанию' },
  { value: 'light', label: 'Светлая' },
  { value: 'dark', label: 'Тёмная' }
];

const ProjectsPage: React.FC = () => {
  const [list, setList] = useState<ProjectDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ProjectDTO | null>(null);
  const [operators, setOperators] = useState<{ id: string; name?: string }[]>([]);
  const [formName, setFormName] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formProjectData, setFormProjectData] = useState<ProjectData>(defaultProjectData);
  const [formRoleIds, setFormRoleIds] = useState<string[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(true);
  const [modalActiveTab, setModalActiveTab] = useState<string>('main');
  const [serviceConnectId, setServiceConnectId] = useState<string | null>(null);
  const [serviceConnectLoading, setServiceConnectLoading] = useState(false);
  const [formServiceMapIds, setFormServiceMapIds] = useState<string[]>([]);
  const [formServiceHosts, setFormServiceHosts] = useState<Record<string, { host_prod: string; host_test: string }>>({});
  const { fetchProjects, setCurrentProjectId } = useProject();
  const navigate = useNavigate();

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await ApiService.getProjects<ProjectDTO[]>();
      setList(Array.isArray(res) ? res : []);
    } finally {
      setLoading(false);
    }
  };

  const loadOperators = async () => {
    setOperatorsLoading(true);
    try {
      const response = await ApiService.getOperators<{ data: { id: string; name?: string }[] }>(uuidv4());
      const list = response?.data;
      setOperators(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn('Не удалось загрузить операторов для проектов:', err);
      setOperators([]);
    } finally {
      setOperatorsLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    loadOperators();
  }, []);

  // Build grouped service list once
  const SERVICE_GROUPS = Array.from(
    new Map(
      SERVICE_NODES.map(n => [
        (n.data as { group: string }).group,
        SERVICE_NODES.filter(s => (s.data as { group: string }).group === (n.data as { group: string }).group)
      ])
    )
  );

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormActive(true);
    setFormProjectData(defaultProjectData);
    setFormRoleIds([]);
    setFormServiceMapIds([]);
    setFormServiceHosts({});
    setServiceConnectId(null);
    setModalActiveTab('main');
    setShowModal(true);
    loadOperators();
  };

  const handleGoToProject = (item: ProjectDTO) => {
    setCurrentProjectId(item.id);
    navigate(paths.home);
    toast.success('Проект «' + (item.name || '') + '» выбран');
  };

  const openEdit = async (item: ProjectDTO) => {
    setEditing(item);
    setFormName(item.name || '');
    setFormActive(item.is_active !== false);
    const pd = item.project_data || {};
    // services для вкладки «Сервисы» не берём из project_data — подгружаются только из API (только конфиг data)
    setFormProjectData({
      ...defaultProjectData,
      ...pd,
      services: ''
    });
    const ids = (item.role || [])
      .map(r => (r as { profile_id?: string }).profile_id || (r as { id?: string }).id)
      .filter(Boolean) as string[];
    setFormRoleIds(ids);
    setFormServiceMapIds([]);
    setFormServiceHosts({});
    setServiceConnectId(null);
    setModalActiveTab('main');
    setShowModal(true);
    loadOperators();
  };

  useEffect(() => {
    if (modalActiveTab !== 'services' || !editing?.id) return;
    let cancelled = false;
    setServiceConnectLoading(true);
    ApiService.getServiceConnectByProjectId<ServiceConnectDTO>(editing.id)
      .then(sc => {
        if (cancelled) return;
        if (sc) {
          const rec = sc as Record<string, unknown>;
          setServiceConnectId((rec.id as string) ?? null);
          // В поле — только конфиг (вложенный data). Если пришла обёртка { data: fullRecord }, берём fullRecord.data
          let configOnly: unknown = rec.data;
          if (configOnly != null && typeof configOnly === 'object' && 'data' in configOnly && !('user_service_grpc_addr' in configOnly)) {
            configOnly = (configOnly as Record<string, unknown>).data;
          }
          // Extract service_map_ids for checkboxes; show rest as JSON
          let configObj: Record<string, unknown> = {};
          if (typeof configOnly === 'string') {
            try { configObj = JSON.parse(configOnly); } catch { configObj = {}; }
          } else if (configOnly != null && typeof configOnly === 'object') {
            configObj = configOnly as Record<string, unknown>;
          }
          const savedIds = Array.isArray(configObj.service_map_ids) ? configObj.service_map_ids as string[] : [];
          setFormServiceMapIds(savedIds);
          // Extract service_hosts for the table
          const savedHosts = (configObj.service_hosts && typeof configObj.service_hosts === 'object')
            ? (configObj.service_hosts as Record<string, { host_prod: string; host_test: string }>)
            : {};
          setFormServiceHosts(savedHosts);
        } else {
          setServiceConnectId(null);
        }
      })
      .catch(() => {
        if (!cancelled) setServiceConnectId(null);
      })
      .finally(() => {
        if (!cancelled) setServiceConnectLoading(false);
      });
    return () => { cancelled = true; };
  }, [modalActiveTab, editing?.id]);

  const handleSaveServices = async () => {
    const projectId = editing?.id;
    if (!projectId) {
      toast.error('Сначала сохраните проект (должен быть project_id).');
      return;
    }
    // Build config from table data (service_map_ids + service_hosts)
    const parsed: Record<string, unknown> = {
      service_map_ids: formServiceMapIds,
      service_hosts: formServiceHosts,
    };
    try {
      if (serviceConnectId) {
        await ApiService.updateServiceConnect(serviceConnectId, {
          project_id: projectId,
          project_name: editing?.name || formName,
          data: parsed,
          is_active: true
        });
      } else {
        const created = await ApiService.addServiceConnect<ServiceConnectDTO>({
          project_id: projectId,
          project_name: editing?.name || formName,
          data: parsed,
          is_active: true
        });
        setServiceConnectId(created.id ?? null);
      }
      toast.success('Конфиг сервисов сохранён.');
    } catch (err: unknown) {
      console.error(err);
      toast.error('Не удалось сохранить конфиг сервисов.');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const role: ProjectRoleEntry[] = formRoleIds.map(profile_id => ({ profile_id }));
      const payload = {
        name: formName,
        is_active: formActive,
        project_data: formProjectData,
        role
      };
      if (editing) {
        await ApiService.updateProject(editing.id, payload);
      } else {
        await ApiService.createProject(payload);
      }
      setShowModal(false);
      await loadList();
      await fetchProjects();
      toast.success(editing ? 'Проект обновлён' : 'Проект создан');
    } catch (err: unknown) {
      console.error(err);
      toast.error(formatProjectError(err));
    }
  };

  const handleDeactivate = async (item: ProjectDTO) => {
    if (!window.confirm('Деактивировать проект «' + item.name + '»?')) return;
    try {
      await ApiService.updateProject(item.id, { is_active: false });
      await loadList();
      await fetchProjects();
      toast.success('Проект деактивирован');
    } catch (err: unknown) {
      console.error(err);
      toast.error(formatProjectError(err));
    }
  };

  const toggleRole = (id: string) => {
    setFormRoleIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const updateProjectData = (key: keyof ProjectData, value: unknown) => {
    setFormProjectData(prev => ({ ...prev, [key]: value }));
  };

  const toggleMenuSection = (label: string) => {
    const enabled = (formProjectData.enabled_menu || []).indexOf(label) >= 0;
    const next = enabled
      ? (formProjectData.enabled_menu || []).filter(l => l !== label)
      : [...(formProjectData.enabled_menu || []), label];
    updateProjectData('enabled_menu', next);
  };

  const toggleMenuSubitem = (sectionLabel: string, childName: string, allChildNames: string[]) => {
    const sub = formProjectData.enabled_submenu || {};
    const current = sub[sectionLabel];
    const effectiveList = !current || current.length === 0 ? allChildNames : current;
    const isChecked = effectiveList.indexOf(childName) >= 0;
    let nextList: string[];
    if (isChecked) {
      nextList = effectiveList.filter(n => n !== childName);
    } else {
      nextList = [...effectiveList, childName];
    }
    const nextSub = { ...sub };
    if (nextList.length === 0 || nextList.length === allChildNames.length) {
      delete nextSub[sectionLabel];
    } else {
      nextSub[sectionLabel] = nextList;
    }
    updateProjectData('enabled_submenu', nextSub);
  };

  const isMenuSectionEnabled = (label: string) =>
    (formProjectData.enabled_menu || []).indexOf(label) >= 0;
  const isMenuSubitemEnabled = (sectionLabel: string, childName: string, _allChildNames?: string[]) => {
    const sub = formProjectData.enabled_submenu || {};
    const current = sub[sectionLabel];
    if (!current || current.length === 0) return true;
    return current.indexOf(childName) >= 0;
  };

  const parseKeyValue = (text: string): Record<string, string> => {
    const out: Record<string, string> = {};
    text.split('\n').forEach(line => {
      const i = line.indexOf('=');
      if (i > 0) {
        const k = line.slice(0, i).trim();
        const v = line.slice(i + 1).trim();
        if (k) out[k] = v;
      }
    });
    return out;
  };

  const toKeyValueText = (obj: Record<string, string> | undefined): string => {
    if (!obj || !Object.keys(obj).length) return '';
    return Object.entries(obj).map(([k, v]) => `${k}=${v}`).join('\n');
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h3 className="mb-0">Справочники</h3>
          <p className="mt-1 mb-0 text-muted small">Управление проектами: настройки, доступ к API и чату, тема админки, сотрудники с доступом.</p>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">Проекты</Card.Title>
          <Button variant="primary" size="sm" onClick={openCreate}>
            + Добавить проект
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4 text-muted">Загрузка...</div>
          ) : (
            <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
              <thead className="table-light">
                <tr>
                  <th>Название</th>
                  <th>Описание</th>
                  <th>Статус</th>
                  <th style={{ width: 120 }}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {list.map(item => (
                  <tr key={item.id}>
                    <td>{item.name || '—'}</td>
                    <td>{(item.project_data as ProjectData)?.description || '—'}</td>
                    <td>
                      <Badge bg={item.is_active !== false ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                        {item.is_active !== false ? 'Активен' : 'Неактивен'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          onClick={() => handleGoToProject(item)}
                          title="Перейти к проекту"
                        >
                          <FaExternalLinkAlt />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          onClick={() => openEdit(item)}
                          title="Изменить"
                        >
                          <FaPencilAlt />
                        </Button>
                        {item.is_active !== false && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                            onClick={() => handleDeactivate(item)}
                            title="Деактивировать"
                          >
                            <FaBan />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr><td colSpan={4} className="text-muted text-center py-3">Нет проектов. Добавьте первый проект.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Редактировать проект' : 'Новый проект'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Tabs
              activeKey={modalActiveTab}
              onSelect={k => k != null && setModalActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="main" title="Основная">
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="small">Название проекта <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        size="sm"
                        value={formName}
                        onChange={e => setFormName(e.target.value)}
                        required
                        placeholder="Название проекта"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small">Статус</Form.Label>
                      <div>
                        <Form.Check
                          type="switch"
                          id="is_active"
                          label="Активен"
                          checked={formActive}
                          onChange={e => setFormActive(e.target.checked)}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small">Описание</Form.Label>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        rows={2}
                        value={formProjectData.description || ''}
                        onChange={e => updateProjectData('description', e.target.value)}
                        placeholder="Описание проекта"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small">Ссылка на иконку проекта (URL)</Form.Label>
                      <Form.Control
                        size="sm"
                        value={formProjectData.icon || ''}
                        onChange={e => updateProjectData('icon', e.target.value)}
                        placeholder="https://... или путь к изображению"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small">Ссылка на брендбук (страница или PDF)</Form.Label>
                      <Form.Control
                        size="sm"
                        value={formProjectData.brand_book || ''}
                        onChange={e => updateProjectData('brand_book', e.target.value)}
                        placeholder="https://taler.uno/wiki/brand-book.html или URL PDF"
                      />
                      <Form.Text className="text-muted">Страница с гайдлайнами бренда или ссылка на PDF.</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small">Ссылка в футере (сайт проекта)</Form.Label>
                      <Form.Control
                        size="sm"
                        value={formProjectData.footer_link || ''}
                        onChange={e => updateProjectData('footer_link', e.target.value)}
                        placeholder="https://teslapay.eu"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small">Email отправителя (from_email)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="email"
                        value={formProjectData.from_email || ''}
                        onChange={e => updateProjectData('from_email', e.target.value)}
                        placeholder="noreply@teslapay.eu"
                      />
                      <Form.Text className="text-muted">Используется в UniOne. Должен быть валидным email. Если пусто — общий дефолт.</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small">Базовый URL ассетов email</Form.Label>
                      <Form.Control
                        size="sm"
                        value={formProjectData.assets_base_url || ''}
                        onChange={e => updateProjectData('assets_base_url', e.target.value)}
                        placeholder="https://apiadmin.test.gsmsoft.eu/email-assets"
                      />
                      <Form.Text className="text-muted">Иконки, логотипы в письмах (PNG/SVG). Без завершающего слеша.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="access" title="Доступы">
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small">Доступ к API (ключ=значение, по одному на строку)</Form.Label>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        rows={3}
                        value={toKeyValueText(formProjectData.api_access)}
                        onChange={e => updateProjectData('api_access', parseKeyValue(e.target.value))}
                        placeholder="api_url=https://... (новая строка) api_key=..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small">Доступ к чату (ключ=значение)</Form.Label>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        rows={2}
                        value={toKeyValueText(formProjectData.chat_access)}
                        onChange={e => updateProjectData('chat_access', parseKeyValue(e.target.value))}
                        placeholder="chat_url=... (новая строка) chat_token=..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small">Сотрудники с доступом к проекту</Form.Label>
                      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6, padding: 8 }}>
                        {operatorsLoading ? (
                          <span className="text-muted small">Загрузка списка операторов...</span>
                        ) : operators.length === 0 ? (
                          <span className="text-muted small">Нет доступных операторов или не удалось загрузить список</span>
                        ) : (
                          operators.map(op => (
                            <Form.Check
                              key={op.id}
                              type="checkbox"
                              id={`role-${op.id}`}
                              label={<span style={{ fontSize: '0.8rem' }}>{op.name || op.id}</span>}
                              checked={formRoleIds.includes(op.id)}
                              onChange={() => toggleRole(op.id)}
                            />
                          ))
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="sections" title="Разделы">
                <Row className="g-3">
                <Col md={12}>
                <Form.Group>
                  <Form.Label className="small">Цветовая тема админки</Form.Label>
                  <Form.Select
                    size="sm"
                    value={formProjectData.theme || ''}
                    onChange={e => updateProjectData('theme', e.target.value)}
                  >
                    {THEME_OPTIONS.map(opt => (
                      <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                </Col>
                <Col md={12}>
                <Form.Group>
                  <Form.Label className="small">Разделы левого меню (какие показывать в проекте)</Form.Label>
                  <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6, padding: 10 }}>
                    {(siteMapRoutes as { label: string; children: { name: string }[] }[]).map(route => {
                      const sectionEnabled = isMenuSectionEnabled(route.label);
                      const childNames = (route.children || []).map(c => c.name);
                      return (
                        <div key={route.label} className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id={`menu-${route.label}`}
                            label={<strong>{route.label}</strong>}
                            checked={sectionEnabled}
                            onChange={() => toggleMenuSection(route.label)}
                          />
                          {childNames.length > 0 && (
                            <div className="ms-4 mt-1">
                              {childNames.map(name => (
                                <Form.Check
                                  key={`${route.label}-${name}`}
                                  type="checkbox"
                                  id={`menu-${route.label}-${name}`}
                                  label={name}
                                  checked={sectionEnabled && isMenuSubitemEnabled(route.label, name)}
                                  disabled={!sectionEnabled}
                                  onChange={() => toggleMenuSubitem(route.label, name, childNames)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Form.Text className="text-muted">Пусто разделов = все разделы. В разделе пусто подразделов = все пункты раздела.</Form.Text>
                </Form.Group>
                </Col>
                </Row>
              </Tab>

              <Tab eventKey="services" title="Сервисы">
                {serviceConnectLoading && (
                  <p className="text-muted mb-2">Загрузка конфига сервисов...</p>
                )}

                {/* Service Map selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Сервисы на карте (Service Map)
                  </Form.Label>
                  <Form.Text className="text-muted d-block mb-2">
                    Отметьте сервисы, которые должны отображаться на карте для этого проекта.
                    Если ничего не выбрано — показываются все сервисы.
                  </Form.Text>
                  <div className="d-flex gap-2 mb-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => setFormServiceMapIds(SERVICE_NODES.map(n => n.id))}>
                      Выбрать все
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setFormServiceMapIds([])}>
                      Снять все
                    </Button>
                  </div>
                  <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6, padding: 10 }}>
                    {SERVICE_GROUPS.map(([group, services]) => (
                      <div key={group} className="mb-3">
                        <div className="fw-semibold text-secondary" style={{ fontSize: 12, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {group}
                        </div>
                        {services.map(svc => {
                          const svcData = svc.data as { label: string };
                          return (
                            <Form.Check
                              key={svc.id}
                              type="checkbox"
                              id={`svc-map-${svc.id}`}
                              label={svcData.label}
                              checked={formServiceMapIds.length === 0 || formServiceMapIds.includes(svc.id)}
                              onChange={() => {
                                const allIds = SERVICE_NODES.map(n => n.id);
                                const current = formServiceMapIds.length === 0 ? allIds : formServiceMapIds;
                                const next = current.includes(svc.id)
                                  ? current.filter(id => id !== svc.id)
                                  : [...current, svc.id];
                                setFormServiceMapIds(next.length === allIds.length ? [] : next);
                              }}
                              disabled={serviceConnectLoading}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </Form.Group>

                {/* Service hosts table */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Адреса сервисов</Form.Label>
                  <Form.Text className="text-muted d-block mb-2">
                    Укажите хосты для prod и test окружений. Заполните только нужные сервисы.
                  </Form.Text>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '34%' }}>Наименование сервиса</th>
                          <th style={{ width: '33%' }}>host_prod</th>
                          <th style={{ width: '33%' }}>host_test</th>
                        </tr>
                      </thead>
                      <tbody>
                        {SERVICE_NODES.map(svc => {
                          const svcData = svc.data as { label: string };
                          const hosts = formServiceHosts[svc.id] ?? { host_prod: '', host_test: '' };
                          return (
                            <tr key={svc.id}>
                              <td className="align-middle py-1 px-2">
                                {svcData.label}
                              </td>
                              <td className="py-1 px-1">
                                <Form.Control
                                  size="sm"
                                  value={hosts.host_prod}
                                  placeholder="prod.example.com"
                                  disabled={serviceConnectLoading}
                                  onChange={e => setFormServiceHosts(prev => ({
                                    ...prev,
                                    [svc.id]: { ...hosts, host_prod: e.target.value }
                                  }))}
                                  style={{ fontSize: 12 }}
                                />
                              </td>
                              <td className="py-1 px-1">
                                <Form.Control
                                  size="sm"
                                  value={hosts.host_test}
                                  placeholder="test.example.com"
                                  disabled={serviceConnectLoading}
                                  onChange={e => setFormServiceHosts(prev => ({
                                    ...prev,
                                    [svc.id]: { ...hosts, host_test: e.target.value }
                                  }))}
                                  style={{ fontSize: 12 }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Form.Group>

                {editing?.id && (
                  <Button variant="primary" size="sm" onClick={handleSaveServices} disabled={serviceConnectLoading}>
                    Сохранить конфиг сервисов
                  </Button>
                )}
                {!editing?.id && (
                  <Form.Text className="text-muted d-block">Сохраните проект, чтобы загружать и сохранять конфиг сервисов.</Form.Text>
                )}
              </Tab>

              {/* Чат — конструктор widget snippet для встраивания на сайт/в
                  приложение клиента. Доку см. support-chat/docs/12-widget-
                  embed-guide.md (шаблон snippet) + docs/13-... (SSO+widget
                  secrets). Реальный widget_shared_secret выдаст chat-backend
                  (admin endpoints §4.2 doc 11 — Этап 6, пока not implemented).
                  Сейчас показываем готовый template + предупреждение что
                  secret нужно запросить. */}
              <Tab eventKey="chat" title="Чат">
                {!editing?.id ? (
                  <Form.Text className="text-muted d-block">
                    Сохраните проект, чтобы получить project_id и сгенерировать widget snippet.
                  </Form.Text>
                ) : (
                  <ChatWidgetTab projectId={editing.id} projectName={editing.name || ''} />
                )}
              </Tab>

              {/* KYC — per-project настройки SumSub WebSDK. Документация:
                  mock_ss/docs/INTEGRATION_FRONTEND.md §«Per-project настройка».
                  Сохраняется в project_data.kyc; kyc-svc применяет после
                  Phase 1 расширения kyc.proto под level_name/project_id. */}
              <Tab eventKey="kyc" title="KYC">
                {!editing?.id ? (
                  <Form.Text className="text-muted d-block">
                    Сохраните проект, чтобы настроить параметры KYC.
                  </Form.Text>
                ) : (
                  <KycSettingsTab
                    projectId={editing.id}
                    data={formProjectData}
                    onChange={patch => setFormProjectData(prev => ({ ...prev, ...patch }))}
                  />
                )}
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)}>
              Отмена
            </Button>
            <Button variant="primary" size="sm" type="submit">
              Сохранить
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectsPage;
